import React, { useEffect, useState } from 'react'
import { useTheme } from '../context/ThemeContext'
const resumePdf = 'https://drive.google.com/file/d/1LtlHkzTAmmK2rlrP3skDwQ1bYamSp8wW/view?usp=drivesdk'


const links = [
  { href: '#home', label: 'Home' },
  { href: '#about', label: 'About' },
  { href: '#skills', label: 'Skills' },
  { href: '#projects', label: 'Projects' },
  { href: '#education', label: 'Education' },
  { href: '#contact', label: 'Contact' },
]


const Navbar = () => {
  const [active, setActive] = useState('#home')
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const { theme, toggleTheme } = useTheme()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const sectionIds = links.map((l) => l.href.replace('#', ''))
    const targets = sectionIds.map((id) => document.getElementById(id)).filter(Boolean)
    if (targets.length === 0) return

    if (typeof IntersectionObserver !== 'function') return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(`#${entry.target.id}`)
        })
      },
      { root: null, rootMargin: '-30% 0px -60% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    )

    targets.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [menuOpen])

  const closeMenu = () => setMenuOpen(false)

  const linkClass = (href) =>
    `block py-2 md:py-0 transition-colors ${
      active === href
        ? 'text-primary font-medium'
        : 'text-slate-600 dark:text-slate-300 hover:text-primary'
    }`

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 ${
        scrolled ? 'shadow-sm' : ''
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <a href="#about" className="text-lg font-semibold text-slate-900 dark:text-white">
          <span className="text-primary">{'<'}</span>
          Omkar Kardel
          <span className="text-primary">{' />'}</span>
        </a>

        <ul className="hidden items-center gap-6 md:flex">
          {links.map((l) => (
            <li key={l.href}>
              <a href={l.href} className={linkClass(l.href)}>
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600 transition-colors hover:border-primary hover:text-primary dark:border-slate-700 dark:text-slate-300"
          >
            {theme === 'dark' ? (
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>

          <a
            href={resumePdf}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden rounded-md bg-primary px-3 py-2 text-sm text-white transition-colors hover:bg-primary-dark sm:inline-block"
          >
            View Resume
          </a>

          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            className="flex h-9 w-9 flex-col items-center justify-center gap-1.5 rounded-md border border-slate-200 md:hidden dark:border-slate-700"
          >
            <span
              className={`block h-0.5 w-5 bg-slate-700 transition-all dark:bg-slate-200 ${
                menuOpen ? 'translate-y-2 rotate-45' : ''
              }`}
            />
            <span
              className={`block h-0.5 w-5 bg-slate-700 transition-all dark:bg-slate-200 ${
                menuOpen ? 'opacity-0' : ''
              }`}
            />
            <span
              className={`block h-0.5 w-5 bg-slate-700 transition-all dark:bg-slate-200 ${
                menuOpen ? '-translate-y-2 -rotate-45' : ''
              }`}
            />
          </button>
        </div>
      </nav>

      <div
        className={`fixed inset-0 top-16 z-40 bg-black/40 transition-opacity md:hidden ${
          menuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={closeMenu}
        aria-hidden="true"
      />

      <div
        className={`absolute left-0 right-0 top-16 z-40 border-b border-slate-200 bg-white px-4 py-4 shadow-lg transition-all duration-300 md:hidden dark:border-slate-800 dark:bg-slate-900 ${
          menuOpen ? 'translate-y-0 opacity-100' : '-translate-y-2 pointer-events-none opacity-0'
        }`}
      >
        <ul className="space-y-1">
          {links.map((l) => (
            <li key={l.href}>
              <a href={l.href} className={linkClass(l.href)} onClick={closeMenu}>
                {l.label}
              </a>
            </li>
          ))}
        </ul>
        <a
          href={resumePdf}
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeMenu}
          className="mt-4 inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm text-white transition-colors hover:bg-primary-dark"
        >
          View Resume
        </a>
      </div>
    </header>
  )
}

export default Navbar
